/**
 * Thinning to one-pixel lines
 *
 * Zhang–Suen thinning, then pruning of spurs shorter than a minimum length and
 * bridging of gaps smaller than a tolerance — a rekhā broken by a crease or a
 * highlight should not become two lines.
 *
 * Binary images are `{ width, height, data: Uint8Array }` of 0/1.
 *
 * © Manish Jagdish Thatte
 */

import { label8, makeBinary } from './image.js';

const N8 = [[0, -1], [1, -1], [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1]];   // P2..P9, clockwise from north

function neighbours(bin, x, y) {
  const { width: w, height: h, data } = bin;
  const out = new Array(8);
  for (let k = 0; k < 8; k++) {
    const xx = x + N8[k][0]; const yy = y + N8[k][1];
    out[k] = xx < 0 || yy < 0 || xx >= w || yy >= h ? 0 : data[yy * w + xx];
  }
  return out;
}

export function countNeighbours(bin, x, y) {
  let c = 0; for (const v of neighbours(bin, x, y)) c += v; return c;
}

/**
 * Crossing number: how many separate arms meet at (x, y). 1 = endpoint,
 * 2 = ordinary line pixel (even on a diagonal), ≥3 = a genuine branch.
 */
export function crossings(bin, x, y) {
  const p = neighbours(bin, x, y);
  let a = 0; for (let k = 0; k < 8; k++) if (!p[k] && p[(k + 1) % 8]) a++;
  return a;
}

/** Zhang–Suen thinning. Returns a new binary image. */
export function zhangSuen(binary) {
  const w = binary.width; const h = binary.height;
  const img = makeBinary(w, h); img.data.set(binary.data);
  let changed = true;
  while (changed) {
    changed = false;
    for (let pass = 0; pass < 2; pass++) {
      const kill = [];
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        if (!img.data[y * w + x]) continue;
        const p = neighbours(img, x, y);            // p[0]=P2 (N) … p[7]=P9 (NW)
        const B = p.reduce((a, b) => a + b, 0);
        if (B < 2 || B > 6) continue;
        let A = 0; for (let k = 0; k < 8; k++) if (!p[k] && p[(k + 1) % 8]) A++;
        if (A !== 1) continue;
        // P2·P4·P6 = 0 and P4·P6·P8 = 0   (first pass);  P2·P4·P8 = 0 and P2·P6·P8 = 0  (second)
        const c1 = pass === 0 ? p[0] * p[2] * p[4] : p[0] * p[2] * p[6];
        const c2 = pass === 0 ? p[2] * p[4] * p[6] : p[0] * p[4] * p[6];
        if (c1 === 0 && c2 === 0) kill.push(y * w + x);
      }
      if (kill.length) { changed = true; for (const i of kill) img.data[i] = 0; }
    }
  }
  return removeStairs(img);
}

/**
 * Zhang–Suen leaves "stair" pixels on diagonal runs: a pixel whose neighbours form a single
 * unbroken arc on the ring (e.g. N, NE, E) is redundant, because the arc already
 * links them. Left in place each becomes a false junction and
 * cuts a clean loop into segments. Removing them (sequentially, so no two
 * neighbours vanish together) preserves connectivity.
 */
function removeStairs(img) {
  const { width: w, height: h, data } = img;
  let changed = true;
  while (changed) {
    changed = false;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (!data[y * w + x]) continue;
      const p = neighbours(img, x, y);
      const B = p.reduce((a, b) => a + b, 0);
      if (B < 3) continue;
      let A = 0; for (let k = 0; k < 8; k++) if (!p[k] && p[(k + 1) % 8]) A++;
      // All neighbours form one unbroken arc, so they stay connected without this pixel.
      if (A === 1 && B < 8) { data[y * w + x] = 0; changed = true; }
    }
  }
  return img;
}

/** Skeleton pixels with exactly one neighbour. */
export function endpoints(skel) {
  const out = [];
  for (let y = 0; y < skel.height; y++) for (let x = 0; x < skel.width; x++) {
    if (skel.data[y * skel.width + x] && countNeighbours(skel, x, y) === 1) out.push([x, y]);
  }
  return out;
}

/** Walk from an endpoint until a junction or another endpoint. Returns `{ path, end: 'junction'|'endpoint' }`. */
function walk(skel, start) {
  const w = skel.width; const path = [start]; const seen = new Set([start[1] * w + start[0]]);
  let [x, y] = start;
  for (;;) {
    let next = null; let count = 0;
    for (const [dx, dy] of N8) {
      const xx = x + dx; const yy = y + dy;
      if (xx < 0 || yy < 0 || xx >= w || yy >= skel.height) continue;
      const i = yy * w + xx;
      if (skel.data[i]) { count++; if (!seen.has(i) && !next) next = [xx, yy]; }
    }
    if (count >= 3 && path.length > 1) return { path, end: 'junction' };
    if (!next) return { path, end: 'endpoint' };
    seen.add(next[1] * w + next[0]); path.push(next); [x, y] = next;
  }
}

/**
 * Remove spurs: branches that run from an endpoint to a junction and are shorter
 * than `minSpur` pixels. A free-standing segment (endpoint to endpoint) is a
 * line, not a spur, and is kept whatever its length.
 */
export function prune(skeleton, minSpur = 8) {
  const out = makeBinary(skeleton.width, skeleton.height); out.data.set(skeleton.data);
  for (let round = 0; round < 3; round++) {
    let removed = false;
    for (const e of endpoints(out)) {
      const { path, end } = walk(out, e);
      if (end === 'junction' && path.length < minSpur) {
        for (let i = 0; i < path.length - 1; i++) out.data[path[i][1] * out.width + path[i][0]] = 0;
        // The last pixel sits on the trunk. Drop it too unless it is what holds
        // two other branches together (its remaining neighbours form ≥2 groups).
        const [jx, jy] = path[path.length - 1];
        const ring = neighbours(out, jx, jy);
        let groups = 0; for (let k = 0; k < 8; k++) if (!ring[k] && ring[(k + 1) % 8]) groups++;
        if (groups < 2) out.data[jy * out.width + jx] = 0;
        removed = true;
      }
    }
    if (!removed) break;
  }
  return out;
}

function drawLine(bin, x0, y0, x1, y1) {
  const dx = Math.abs(x1 - x0); const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1; const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy; let x = x0; let y = y0;
  for (;;) {
    bin.data[y * bin.width + x] = 1;
    if (x === x1 && y === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x += sx; }
    if (e2 <= dx) { err += dx; y += sy; }
  }
}

/**
 * Bridge gaps: join an endpoint to the nearest pixel of a *different* connected
 * piece within `maxGap` pixels, provided that pixel lies roughly ahead of the
 * line's heading (within ~60°). Gaps larger than `maxGap` are left alone.
 */
export function bridgeGaps(skeleton, maxGap = 6) {
  const out = makeBinary(skeleton.width, skeleton.height); out.data.set(skeleton.data);
  const { labels } = label8(out); const w = out.width; const h = out.height;
  for (const [ex, ey] of endpoints(skeleton)) {
    const { path } = walk(skeleton, [ex, ey]);
    const back = path[Math.min(path.length - 1, 5)];
    let hx = ex - back[0]; let hy = ey - back[1]; const hn = Math.hypot(hx, hy) || 1; hx /= hn; hy /= hn;
    const mine = labels[ey * w + ex];
    let best = null; let bestD = Infinity;
    const r = Math.ceil(maxGap);
    for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
      const xx = ex + dx; const yy = ey + dy;
      if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
      const l = labels[yy * w + xx];
      if (!l || l === mine) continue;
      const d = Math.hypot(dx, dy); if (d > maxGap || d >= bestD) continue;
      if (path.length > 2 && (dx * hx + dy * hy) / d < 0.5) continue;
      best = [xx, yy]; bestD = d;
    }
    if (best) drawLine(out, ex, ey, best[0], best[1]);
  }
  return out;
}
